<template>
  <div id="EOI" class="EOI container">
    <div class="EOI-heading">
      <h1>
        EXPRESSION
        <br />
        <span class="EOI-of" style>OF</span>INTEREST
      </h1>
    </div>
    <div class="EOI-info">
      <p>
        Traitz tool is currently in a closed beta and we are working towards an
        open beta release soon.
      </p>

      <p>
        In the meantime we are working with a select number of artists as part
        of the closed beta group.
      </p>

      <p>
        If you’d like to join the closed beta group, please fill out the form
        below with information about you and your project and we will review if
        it is suitable for the closed beta program.
      </p>
    </div>

    <div class="EOI-form">
      <form class @submit.prevent="submit">
        <div class="form-field">
          <input
            id="_name"
            ref="name"
            v-model="form.name"
            type="text"
            name="name"
            placeholder="Full Name*"
            :class="nameIsValid ? 'valid-input' : 'invalid-input'"
          />
          <p v-show="!nameIsValid" class="invalid-msg">
            Please enter your name
          </p>
        </div>
        <div class="form-field">
          <input
            id="_email"
            ref="email"
            v-model="form.email"
            type="email"
            name="email"
            placeholder="Email*"
            :class="emailIsValid ? 'valid-input' : 'invalid-input'"
          />
          <p v-show="!emailIsValid" class="invalid-msg">
            Please enter a valid email address
          </p>
        </div>
        <div class="form-field">
          <input
            id="_website"
            v-model="form.website"
            type="text"
            placeholder="Website*"
            name="website"
            :class="websiteIsValid ? 'valid-input' : 'invalid-input'"
          />
          <p v-show="!websiteIsValid" class="invalid-msg">
            Please enter your website
          </p>
        </div>
        <div class="form-field">
          <textarea
            id="_message"
            v-model="form.message"
            type="text"
            placeholder="Tell us about your project"
            name="message"
            class="valid-input"
          />
        </div>
        <p class="form-recaptcha-terms">
          This site is protected by reCAPTCHA and the Google
          <a href="https://policies.google.com/privacy">Privacy Policy</a> and
          <a href="https://policies.google.com/terms">Terms of Service</a>
          apply.
        </p>
        <p v-show="formSuccessful" class="successful-submission">
          Thank you for your submission. Our team members will be in touch.
        </p>
        <p v-show="formFailed" class="successful-submission">
          Error: {{ formFailed.message }}
        </p>
        <div class="form-submitcontainer">
          <input
            :class="{
              ['form-submit']: true,
              ['form-submit--submitting']: formSubmitting,
            }"
            :placeholder="submitButtonText"
            type="submit"
          />
          <div v-show="formSubmitting" class='form-spinner'>
            <div class="lds-ring"><div></div><div></div><div></div><div></div></div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, toRaw, computed, nextTick } from "vue";
import { useReCaptcha } from "vue-recaptcha-v3";

const formSubmitting = ref(false);
const submitButtonText = computed(() => { 
  if (formSuccessful.value) return 'Sent'
  if (formSubmitting.value) return '';
  return 'Submit';
});
const form = reactive({ name: "", email: "", website: "", message: "" });
const triedSubmit = ref(false);
const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
const emailIsValid = computed(
  () => !triedSubmit.value || EMAIL_REGEX.test(form.email)
);
const nameIsValid = computed(() => !triedSubmit.value || form.name != "");
const websiteIsValid = computed(() => !triedSubmit.value || form.website != "");
const formSuccessful = ref(false);
const formFailed = ref("");

const { executeRecaptcha, recaptchaLoaded } = useReCaptcha();

const submit = () => {
  // (optional) Wait until recaptcha has been loaded.
  triedSubmit.value = true;
  nextTick(async () => {
    if (nameIsValid.value && emailIsValid.value && websiteIsValid.value) {
      let token;
      if (!window.location.host.includes("localhost")) {
        formSubmitting.value = true;

        console.log("captcha loading");
        await recaptchaLoaded();
        console.log("captcha loaded");

        // Execute reCAPTCHA with action "login".
        token = await executeRecaptcha("submit");
        console.log("captcha completed ", token);
      }
      const body = {
        captchaToken: token,
        ...toRaw(form),
      };


      const fetchHost = window.location.host.includes("localhost")
        ? "http://localhost:3000/dev"
        : process.env.VUE_APP_API_URL;
      const response = await fetch(`${fetchHost}/expression-of-interest`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify(body),
      });

      console.log("response", response);

      // show loading before showing success or failure message
      if (response.status != 200) {
        const errorData = await response.json();
        console.log(errorData);
        formFailed.value = errorData;
      } else {
        formSuccessful.value = true;
      }

      formSubmitting.value = false;
    }
  });
};
</script>

<style lang="scss">
@import "@/styles/styles.scss";
.invalid-input {
  border: 1px solid red;
}
.valid-input {
  border: 1px solid #d1d1d1;
}
.invalid-msg {
  max-width: 900px;
  width: 100%;
  text-align: left;
  color: red;
  font-style: italic;
  font-weight: 300;
  margin: auto;
  margin-top: -14px;
  font-size: 14px;
  display: flex;
  align-self: flex-start;
  @include media(">=tablet") {
    font-size: 18px;
    margin-top: -20px;
  }
}
.successful-submission {
  margin: 50px auto;
  text-align: center;
  @include media(">=tablet") {
    margin: 100px auto;
  }
}

.lds-ring {
  display: inline-block;
  position: relative;
  width: 80px;
  height: 80px;
}
.lds-ring div {
  box-sizing: border-box;
  display: block;
  position: absolute;
  width: 64px;
  height: 64px;
  margin: 8px;
  border: 8px solid #fff;
  border-radius: 50%;
  animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
  border-color: #fff transparent transparent transparent;
}
.lds-ring div:nth-child(1) {
  animation-delay: -0.45s;
}
.lds-ring div:nth-child(2) {
  animation-delay: -0.3s;
}
.lds-ring div:nth-child(3) {
  animation-delay: -0.15s;
}
@keyframes lds-ring {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}


@keyframes loading {
  to {
    -webkit-transform: rotate(360deg);
  }
}
@-webkit-keyframes loading {
  to {
    -webkit-transform: rotate(360deg);
  }
}

.EOI {
  .EOI-heading {
    // margin: 75px auto;
    padding: 75px 0 50px 0;
    width: 100%;
    @include media(">=tablet") {
      // margin: 150px auto;
      padding: 150px 0 100px 0;
    }
    h1 {
      width: 100%;
      text-align: right;
      border-left: 4px solid #fff;
      position: relative;
      // max-width: 575px;
      max-width: 290px;
      margin: auto;
      // font-size: 80px;
      font-size: 40px;
      letter-spacing: 0.08em;
      line-height: 1;
      @include media(">=tablet") {
        max-width: 488px;
        font-size: 68px;
      }
      .EOI-of {
        font-size: 20px;
        position: absolute;
        // top: 87px;
        top: 42px;
        left: 30px;
        // left: 55px;
        @include media(">=tablet") {
          font-size: 30px;
          top: 74px;
          left: 55px;
        }
      }
    }
  }
  .EOI-info {
    max-width: 900px;
    width: 100%;
    margin: auto;
    p {
      font-size: 20px;
      margin: 25px 0;
      @include media(">=tablet") {
        margin: 50px 0;
        font-size: 32px;
      }
    }
  }
}
.form input:focus {
  outline: none;
}
.form {
  position: relative;
  &-field {
    max-width: 900px;
    width: 100%;
    margin: auto;

    input,
    textarea {
      font-family: "Roboto";
      max-width: 900px;
      height: 50px;
      width: 100%;
      margin: 15px auto;
      background: #232323;
      border-radius: 4px;
      font-size: 18px;
      font-weight: 300;
      color: white;
      padding-left: 20px;
      @include media(">tablet") {
        font-size: 28px;
        height: 79px;
        margin: 25px auto;
        padding-left: 20px;
      }
    }
    input::placeholder {
      color: #8a8a8a;
    }
    textarea {
      height: 200px !important;
      padding-top: 20px;
    }
    textarea::placeholder {
      font-family: "Roboto";
      color: #8a8a8a;
    }
  }
  &-submit {
    cursor: pointer;
    background: #6f10cf;
    width: 250px;
    height: 66px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 20px;
    font-weight: 700;
    border: none;
    color: white;
    @include media(">tablet") {
      font-size: 28px;
      width: 350px;
      height: 86px;
    }
    &:hover {
      background: #7f18e6;
    }
    &:active {
      background: #5f0bb3;
    }
    &--submitting {
      pointer-events: none;
      opacity: 0.8;
    }
  }
  &-submitcontainer {
    position: relative;
    margin: 50px auto;
    width: fit-content;
    @include media(">tablet") {
      margin: 100px auto;
    }
  }
  &-recaptcha-terms {
    font-size: 12px;
    // margin-top: -5px;
    // margin-bottom: -5px;
    margin: -5px auto;
    max-width: 900px;
    width: 100%;
    @include media(">tablet") {
      //     margin-top: -20px;
      // margin-bottom: -20px;
      margin: -20px auto;
    }
  }
  &-submitcontainer {
    position: relative;
  }

  &-spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}

.grecaptcha-badge {
  visibility: hidden;
}
</style>
